import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { FormGroup, FormControl, FormArray, FormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { Observable, of, iif, tap, switchMap, debounceTime, filter, finalize, first, BehaviorSubject, map } from 'rxjs';
import { Lead } from 'src/app/core/models/lead/lead.model';
import { APP_CONFIG } from 'src/app/shared/configurations/configurations.factory';
import { Configurations } from 'src/app/shared/configurations/models/configurations.model';
import { LeadDryrunError } from 'src/app/shared/utils/pipes/dryrun-lead-error.pipe';
import { ContactValidations } from 'src/app/shared/utils/validations/contact.validations';
import { FiscalIdValidations } from 'src/app/shared/utils/validations/fiscal-id.validations';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { GlobalSpinnerActions } from '../../global-spinner/actions-types';
import { SearchSuggestion } from '../../inputs/autocomplete/search-strategy-base';
import { LeadFormBase } from '../lead-form-base';
import { LeadInfoDetailsForm } from '../lead-info-form/lead-info-form.component';
import { LeadCreateFormService } from './services/lead-create-form.service';
import { LobbyErrorHandlerService } from 'src/app/core/services/lobby-error-handler.service';
import { AutocompleteBusinessSectorSuggestionService } from 'src/app/shared/common-features/services/autocomplete-strategies/autocomplete-business-sector-suggestion.service';
import { AutocompleteProvinceSuggestionService } from 'src/app/shared/common-features/services/autocomplete-strategies/autocomplete-province-suggestion.service';
import { HumanizePhonePipe } from 'src/app/shared/utils/pipes/humanize-phone.pipe';
import { LobbyErrorCode } from 'src/app/infrastructure/lobby-proxy/enums/lobby-error-code.enum';
import { merge, omit } from 'lodash';
import { LeadActions } from 'src/app/core/events/lead.actions';

const phoneUtils = PhoneNumberUtil.getInstance()
const typingDebounceTime = 1000

export interface LeadCreateDetailsForm extends FormGroup {
  controls: {
    businessName: FormControl<string | null>
    taxName: FormControl<string | null>
    piva: FormControl<string | null>
    italianFiscalCode: FormControl<string | null>
    municipality: FormControl<string | null>
    province: FormControl<string | null>
    businessSector: FormControl<string | null>

    contacts: FormArray<FormGroup<{ description: FormControl<string | null>, value: FormControl<string | null>, }>>
    rawvalues: FormArray<FormGroup<{ description: FormControl<string | null>, value: FormControl<string | null>, }>>

    privacyAccepted: FormControl<boolean | null>
    acknowledgmentAccepted: FormControl<boolean | null>
    marketingAccepted: FormControl<boolean | null>

    comment: FormControl<string | null>
  }
}

@Component({
    selector: 'lead-create-form',
    templateUrl: './lead-create-form.component.html',
    styleUrls: ['./lead-create-form.component.scss'],
    standalone: false
})
export class LeadCreateFormComponent extends LeadFormBase<LeadCreateDetailsForm> implements OnInit {

  @Input() leadFactory!: () => Lead

  @Output() dirty = new EventEmitter<boolean>()
  @Output() cancel = new EventEmitter<void>()
  @Output() leadCreated = new EventEmitter<void>()

  get lead(): Lead | null { return this._lead! }

  newContact = new FormGroup({ description: new FormControl<string>('', [Validators.required, Validators.maxLength(255)]), value: new FormControl<string>('', [ContactValidations.contactFormValidator(), Validators.required, Validators.maxLength(255)]) })
  newRawvalue = new FormGroup({ description: new FormControl<string>('', [Validators.required, Validators.maxLength(255)]), value: new FormControl<string>('', [Validators.required, Validators.maxLength(1024)]) })

  lobbyErrors$ = new BehaviorSubject<LeadDryrunError[]>([])

  private _nonValuableLobbyErrors: LobbyErrorCode[] = [LobbyErrorCode.InvalidLogic]
  private _filterLobbyError: boolean = false

  constructor(private readonly appStore: Store,
    @Inject(APP_CONFIG) config: Configurations,
    private readonly dialog: MatDialog,
    private readonly leadCreateFormService: LeadCreateFormService,
    readonly autocompleteProvinceSuggestionService: AutocompleteProvinceSuggestionService,
    readonly autocompleteBusinessSectorSuggestionService: AutocompleteBusinessSectorSuggestionService,
    private readonly cdr: ChangeDetectorRef,
    private readonly errorHandlerService: LobbyErrorHandlerService,
    humanizePhonePipe: HumanizePhonePipe) {
    super(config, 1000, humanizePhonePipe)
    this.form = this.createForm()
    this.initLobbyValidation(this.form)
    this.subscribeToForm(this.form)
  }

  ngOnInit(): void {
    this._lead = this.leadFactory()
  }

  onSelectedProvinceSuggestion(suggestion: SearchSuggestion | null) {

    let lobbyValidationsOrEmpty$: Observable<any> = of(void 0)

    iif(() => !!suggestion,
      this.leadCreateFormService.getProvinces$(Number(suggestion?.id)).pipe(
        tap(province => {
          this.lead!.province = province
          if (this.lead!.municipality?.province.id != province?.id) {
            this.lead!.municipality = null
            this.form!.controls.municipality.setValue('', { emitEvent: false })
          }
        }),
        tap(() => lobbyValidationsOrEmpty$ = this.lobbyValidations$())
      ),
      of(void 0).pipe(
        tap(() => {
          const wasProvinceValued = !!this.lead!.province
          const wasMunicipalityValued = !!this.lead!.municipality
          this.lead!.province = null
          if (wasProvinceValued && !wasMunicipalityValued) lobbyValidationsOrEmpty$ = this.lobbyValidations$()
        })
      )
    ).pipe(
      tap(() => { this.form!.updateValueAndValidity(); this.form!.markAsDirty(); }),
      switchMap(() => lobbyValidationsOrEmpty$),
      this.errorHandlerService.handleError
    ).subscribe()

  }

  onSelectedMunicipalitySuggestion(suggestion: SearchSuggestion | null) {
    let lobbyValidationsOrEmpty$: Observable<any> = of(void 0)

    iif(() => !!suggestion,
      this.leadCreateFormService.getMunicipality$(Number(suggestion?.id)).pipe(
        tap(municipality => {
          this.lead!.municipality = municipality
          this.lead!.province = municipality.province
          this.form!.controls.province.setValue(municipality.province.name, { emitEvent: false })
        }),
        tap(() => lobbyValidationsOrEmpty$ = this.lobbyValidations$())
      ),
      of(void 0).pipe(
        tap(() => {
          const wasMunicipalityValued = !!this.lead!.municipality
          this.lead!.municipality = null
          if (wasMunicipalityValued) lobbyValidationsOrEmpty$ = this.lobbyValidations$()
        })
      )
    ).pipe(
      tap(() => { this.form!.updateValueAndValidity(); this.form!.markAsDirty(); }),
      switchMap(() => lobbyValidationsOrEmpty$),
      this.errorHandlerService.handleError
    ).subscribe()
  }

  onSelectedBusinessSectorSuggestion(suggestion: SearchSuggestion | null) {

    let lobbyValidationsOrEmpty$: Observable<any> = of(void 0)

    iif(() => !!suggestion,
      this.leadCreateFormService.getBusinessSector$(Number(suggestion?.id)).pipe(
        tap(businessSector => this.lead!.businessSector = businessSector),
        tap(() => lobbyValidationsOrEmpty$ = this.lobbyValidations$())
      ),
      of(void 0).pipe(
        tap(() => {
          const wasBusinessSectorValued = !!this.lead!.businessSector
          this.lead!.businessSector = null
          if (wasBusinessSectorValued) lobbyValidationsOrEmpty$ = this.lobbyValidations$()
        })
      )
    ).pipe(
      tap(() => this.form!.updateValueAndValidity()),
      switchMap(() => lobbyValidationsOrEmpty$),
      this.errorHandlerService.handleError
    ).subscribe()
  }

  onResetClick() {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, ConfirmDialogComponent.createConfigurations(
      {
        title: 'Conferma',
        message: 'Se procedi le modifiche saranno perdute',
        confirmButtonText: 'Procedi',
        cancelButtonText: 'Annulla'
      }
    ))

    confirmDialog.afterClosed().subscribe(result => {
      if (result) {
        this.form = this.createForm()
        this.initLobbyValidation(this.form)
        this.subscribeToForm(this.form)
        this._lead = this.leadFactory()
        this.cdr.markForCheck()
      }
    })
  }

  onCreateClick() {
    of(void 0).pipe(
      tap(() => this.appStore.dispatch(GlobalSpinnerActions.showGlobalSpinner())),
      switchMap(() => this.leadCreateFormService.createLead$(this.lead!)),
      tap(() => this.appStore.dispatch(LeadActions.leadCreated(this.lead!))),
      this.errorHandlerService.handleError,
      finalize(() => this.appStore.dispatch(GlobalSpinnerActions.hideGlobalSpinner())),
      tap(() => this.leadCreated.emit())
    ).subscribe()
  }

  override createForm(): LeadCreateDetailsForm {
    return new FormBuilder().group(
      {
        piva: new FormControl<string>('', [FiscalIdValidations.pivaFormValidator()]),
        italianFiscalCode: new FormControl<string>('', [FiscalIdValidations.italianFiscalCodeFormValidator()]),

        municipality: new FormControl<string>(''),
        province: new FormControl<string>(''),
        businessSector: new FormControl<string>(''),

        contacts: new FormArray<FormGroup<{ description: FormControl<string | null>, value: FormControl<string | null>, }>>([]),
        rawvalues: new FormArray<FormGroup<{ description: FormControl<string | null>, value: FormControl<string | null>, }>>([]),

        privacyAccepted: new FormControl<boolean>(false),
        acknowledgmentAccepted: new FormControl<boolean>(false),
        marketingAccepted: new FormControl<boolean>(false),

        businessName: new FormControl<string>('', [Validators.maxLength(1024)]),
        taxName: new FormControl<string>('', [Validators.maxLength(1024)]),

        comment: new FormControl<string>('')
      }
    )
  }

  private initLobbyValidation(form: LeadCreateDetailsForm) {

    this.lobbyErrors$ = new BehaviorSubject<LeadDryrunError[]>([])

    // Triggers

    form.controls.businessName.valueChanges
      .pipe(
        debounceTime(typingDebounceTime),
        filter(() => form.controls.businessName.valid),
        switchMap(() => this.lobbyValidations$())
      )
      .subscribe()

    form.controls.taxName.valueChanges
      .pipe(
        debounceTime(typingDebounceTime),
        filter(() => form.controls.taxName.valid),
        switchMap(() => this.lobbyValidations$())
      )
      .subscribe()

    form.controls.piva.valueChanges
      .pipe(
        debounceTime(typingDebounceTime),
        filter(() => form.controls.piva.valid),
        switchMap(() => this.lobbyValidations$())
      )
      .subscribe()

    form.controls.italianFiscalCode.valueChanges
      .pipe(
        debounceTime(typingDebounceTime),
        filter(() => form.controls.italianFiscalCode.valid),
        switchMap(() => this.lobbyValidations$())
      )
      .subscribe()

    form.controls.comment.valueChanges
      .pipe(
        debounceTime(typingDebounceTime),
        filter(() => form.controls.comment.valid),
        switchMap(() => this.lobbyValidations$())
      )
      .subscribe()

    form.controls.contacts.valueChanges
      .pipe(
        debounceTime(typingDebounceTime / 2),
        switchMap(() => this.lobbyValidations$())
      )
      .subscribe()

    form.controls.rawvalues.valueChanges
      .pipe(
        debounceTime(typingDebounceTime / 2),
        switchMap(() => this.lobbyValidations$())
      )
      .subscribe()

    // Observers
    // Duplicates contact point
    this.lobbyErrors$.pipe(
      filter(() => !this._filterLobbyError),
      tap(() => this.form!.controls.contacts.controls.forEach(c => {
        c.setErrors(omit(c.errors, ['dryrunDuplicate']))
        c.updateValueAndValidity({ onlySelf: false, emitEvent: false })
      })),
      tap(() => this.cdr.markForCheck()),
      map(errors => errors.filter(e => e.errorCode == LobbyErrorCode.LeadContactPointDuplicate)),
      filter(errors => !!errors.length)
    ).subscribe(contactDuplicateErrors => {
      this.form!.controls.contacts.controls
        .filter(c => contactDuplicateErrors.map(e => e.message).some(v => phoneUtils.isNumberMatch(v, c.controls.value.value!) >= 3 || v.trim().toLowerCase() == c.controls.value.value!.trim().toLowerCase()))
        .forEach(c => c.setErrors(merge(c.errors, { dryrunDuplicate: true })))
    })

    // Piva
    this.lobbyErrors$.pipe(
      filter(() => !this._filterLobbyError),
      tap(() => {
        this.form!.controls.piva.setErrors(omit(this.form!.controls.piva.errors, ['lobbyInvalid']))
        this.form!.controls.piva.updateValueAndValidity({ onlySelf: false, emitEvent: false })
      }),
      tap(() => this.cdr.markForCheck()),
      map(errors => errors.filter(e => e.errorCode == LobbyErrorCode.PartitaIvaInvalid)),
      filter(errors => !!errors.length)
    ).subscribe(() => this.form!.controls.piva.setErrors(merge(this.form!.controls.piva.errors, { lobbyInvalid: true })))

    // Italian fiscal code
    this.lobbyErrors$.pipe(
      filter(() => !this._filterLobbyError),
      tap(() => {
        this.form!.controls.italianFiscalCode.setErrors(omit(this.form!.controls.italianFiscalCode.errors, ['lobbyInvalid']))
        this.form!.controls.italianFiscalCode.updateValueAndValidity({ onlySelf: false, emitEvent: false })
      }),
      tap(() => this.cdr.markForCheck()),
      map(errors => errors.filter(e => e.errorCode == LobbyErrorCode.CodiceFiscalePersonaFisicaInvalid)),
      filter(errors => !!errors.length)
    ).subscribe(() => this.form!.controls.italianFiscalCode.setErrors(merge(this.form!.controls.italianFiscalCode.errors, { lobbyInvalid: true })))

    // Invalid rawvalues
    this.lobbyErrors$.pipe(
      filter(() => !this._filterLobbyError),
      tap(() => this.form!.controls.rawvalues.controls.forEach(r => {
        r.setErrors(omit(r.errors, ['lobbyInvalid']))
        r.updateValueAndValidity({ onlySelf: false, emitEvent: false })
      })),
      tap(() => this.cdr.markForCheck()),
      map(errors => errors.filter(e => e.errorCode == LobbyErrorCode.RawValueInvalid)),
      filter(errors => !!errors.length)
    ).subscribe(rawvalueErrors => {
      this.form!.controls.rawvalues.controls
        .filter(r => rawvalueErrors.map(e => e.message).some(v => v.trim().toLowerCase() == r.controls.value.value!.trim().toLowerCase()))
        .forEach(r => r.setErrors(merge(r.errors, { lobbyInvalid: true })))
    })

    // Business sector
    this.lobbyErrors$.pipe(
      filter(() => !this._filterLobbyError),
      tap(() => {
        this.form!.controls.businessSector.setErrors(omit(this.form!.controls.italianFiscalCode.errors, ['lobbyInvalid']))
        this.form!.controls.businessSector.updateValueAndValidity({ onlySelf: false, emitEvent: false })
      }),
      tap(() => this.cdr.markForCheck()),
      map(errors => errors.filter(e => e.errorCode == LobbyErrorCode.BusinessSectorAtecoCodeInvalid)),
      filter(errors => !!errors.length)
    ).subscribe(() => this.form!.controls.businessSector.setErrors(merge(this.form!.controls.businessSector.errors, { lobbyInvalid: true })))

  }

  override subscribeToForm(form: LeadInfoDetailsForm) {

    form.controls.businessName.valueChanges
      .subscribe(value => this.lead!.businessName = value!)

    form.controls.taxName.valueChanges
      .subscribe(value => this.setTaxName(value!))

    form.controls.piva.valueChanges
      .subscribe(value => this.setPiva(value!))

    form.controls.italianFiscalCode.valueChanges
      .subscribe(value => this.setItalianFiscalCode(value!))

    form.controls.comment.valueChanges
      .subscribe(value => this.lead!.comment = value!)

    form.controls.rawvalues.controls.forEach(c =>
      c.controls.value.valueChanges.subscribe(v =>
        this.filterConfiguredRawValues(this.lead!.rawValues)[form.controls.rawvalues.controls.indexOf(c)].value = v!
      )
    )

    form.controls.privacyAccepted.valueChanges
      .subscribe(value => this.setPrivacyAccepted(value!))

    form.controls.acknowledgmentAccepted.valueChanges
      .subscribe(value => this.setAcknowledgmentAccepted(value!))

    form.controls.marketingAccepted.valueChanges
      .subscribe(value => this.setMarketingAccepted(value!))

    form.valueChanges.pipe(
      filter(() => form.dirty),
      first()
    ).subscribe(() => this.dirty.emit(true))
  }

  private setPrivacyAccepted(value: boolean) {
    const existingPrivacyAcceptedRawvalue = this.getPrivacyAccepted(this._lead!)

    if (existingPrivacyAcceptedRawvalue) existingPrivacyAcceptedRawvalue.value = value.toString()
    else this._lead!.rawValues.push({
      id: 0, description: this.config.rawvaluesNamesConfig.privacyAccepted[0],
      value: value.toString(),
      created: new Date(), updated: new Date()
    })
  }

  private setAcknowledgmentAccepted(value: boolean) {
    const existingAcknowledgmentAcceptedRawvalue = this.getAcknowledgmentAccepted(this._lead!)

    if (existingAcknowledgmentAcceptedRawvalue) existingAcknowledgmentAcceptedRawvalue.value = value.toString()
    else this._lead!.rawValues.push({
      id: 0, description: this.config.rawvaluesNamesConfig.acknowledgmentAccepted[0],
      value: value.toString(),
      created: new Date(), updated: new Date()
    })
  }

  private setMarketingAccepted(value: boolean) {
    const existingMarketingAcceptedRawvalue = this.getMarketingAccepted(this._lead!)

    if (existingMarketingAcceptedRawvalue) existingMarketingAcceptedRawvalue.value = value.toString()
    else this._lead!.rawValues.push({
      id: 0, description: this.config.rawvaluesNamesConfig.marketingAccepted[0],
      value: value.toString(),
      created: new Date(), updated: new Date()
    })
  }

  private lobbyValidations$() {
    return of(void 0).pipe(
      debounceTime(1), // necessary for race condition with non async form operations
      switchMap(() => this.leadCreateFormService.createLeadDryRun$(this.lead!)),
      map(result => {
        if ((<LeadDryrunError[]>result).length !== undefined)
          this.lobbyErrors$.next((result as LeadDryrunError[]).filter(e => !this._nonValuableLobbyErrors.includes(e.errorCode)))
        else this.lobbyErrors$.next([])
      })
    )
  }

}
